import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';

import { type Dispatch, type SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import L, { type MarkerCluster } from 'leaflet';
import { useMap } from 'react-leaflet';

import type { Target } from '../../tmp';
import { getMarkerData, getMarkerDirection } from './markers/TargetMarkerChooser';
import { ctxPluto } from '@/contexts/PlutoContext';

import './TargetMarkers.css';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { isValidLatLong } from '@/utilities/latlong';
import type { MinimalTarget } from '@/generated/model';

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return character;
    }
  });
}

export function getTargetTooltipContent(
  target: MinimalTarget,
  currentPosition: NonNullable<Target['currentPosition']>
): string {
  const description =
    target.description && target.description !== target.name
      ? `<span class="tooltip-subtitle">${escapeHtml(target.description)}</span>`
      : '';
  const bubbleContent = currentPosition.bubbleContent.length
    ? `<span class="tooltip-bubble-content">${currentPosition.bubbleContent
        .map((content) => `<span class="tooltip-bubble-line">${escapeHtml(content)}</span>`)
        .join('')}</span>`
    : '';

  return `${escapeHtml(target.name)}${description}${bubbleContent}`;
}

export function TargetMarkers({
  fullTargets,
  selectedTargetId,
  setSelectedTargetId,
}: {
  fullTargets: Target[] | undefined;
  selectedTargetId: number | undefined;
  setSelectedTargetId: Dispatch<SetStateAction<number | undefined>>;
}) {
  const ctx = useContext(ctxPluto);
  const map = useMap();
  const markers = useRef(new Map<number, L.Marker>());
  const hoveredTargetId = useRef<number | undefined>(undefined);
  const selectedTargetIdRef = useRef(selectedTargetId);
  const popupTargetIdRef = useRef(ctx?.popupTargetId);
  const [clusterLayer, setClusterLayer] = useState<L.MarkerClusterGroup | undefined>(undefined);
  const MARKER_ICON_SIZE = 32;
  const CLUSTER_ICON_SIZE = 36;

  function bringTooltipToFront(targetId: number | undefined) {
    if (targetId === undefined) return;

    markers.current.get(targetId)?.getTooltip()?.bringToFront();
  }

  function bringTargetTooltipsToFront() {
    // Apply the lower priorities first so the hovered target stays on top.
    bringTooltipToFront(popupTargetIdRef.current);
    bringTooltipToFront(selectedTargetIdRef.current);
    bringTooltipToFront(hoveredTargetId.current);
  }

  function setupTooltipElement(tooltip: L.Tooltip, targetId: number, targetName: string) {
    const element = tooltip.getElement();
    if (!element) return;

    element.setAttribute('data-qa-element-id', `target_tooltip_${targetName}`);

    if (element.dataset.targetTooltipHoverBound === 'true') return;

    element.dataset.targetTooltipHoverBound = 'true';
    element.addEventListener('mouseenter', () => {
      if (isPhoneOrHighScreen()) return;

      hoveredTargetId.current = targetId;
      bringTargetTooltipsToFront();
    });
    element.addEventListener('mouseleave', () => {
      if (hoveredTargetId.current === targetId) {
        hoveredTargetId.current = undefined;
      }
      bringTargetTooltipsToFront();
    });
  }

  useEffect(() => {
    selectedTargetIdRef.current = selectedTargetId;
    popupTargetIdRef.current = ctx?.popupTargetId;
  }, [selectedTargetId, ctx?.popupTargetId]);

  useEffect(() => {
    if (!map) return;

    function createClusterIcon(cluster: MarkerCluster) {
      return L.divIcon({
        html: `${cluster.getChildCount()}`,
        className: 'leaflet-custom-cluster-icon',
        iconSize: [CLUSTER_ICON_SIZE, CLUSTER_ICON_SIZE],
      });
    }
    const cl = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: false,
      zoomToBoundsOnClick: true,
      disableClusteringAtZoom: 17,
      iconCreateFunction: createClusterIcon,
    });
    setClusterLayer(cl);
    map.addLayer(cl);
  }, [map]);

  useEffect(() => {
    if (!map || !clusterLayer) return;

    // ami kikerult torolni kell
    markers.current.forEach((marker, id) => {
      if (!fullTargets || !fullTargets.find((entry) => entry.target.id === id)) {
        clusterLayer.removeLayer(marker);
        markers.current.delete(id);
      }
    });

    if (!fullTargets?.some((entry) => entry.target.id === hoveredTargetId.current)) {
      hoveredTargetId.current = undefined;
    }

    const radToDeg = (rad: number): number => {
      return rad * (180.0 / Math.PI);
    };

    function getMarkerHtml(target: Target) {
      if (!target.currentPosition) return '';

      let markerHTML;
      if (target.currentPosition.icon) {
        markerHTML = getMarkerData(
          target.currentPosition.icon,
          MARKER_ICON_SIZE,
          MARKER_ICON_SIZE,
          target.currentPosition.color
        );
      } else {
        markerHTML = getMarkerData(
          target.target.category,
          MARKER_ICON_SIZE,
          MARKER_ICON_SIZE,
          target.currentPosition.color
        );
      }

      const directionRad = target.currentPosition.direction;
      if (!isNaN(directionRad)) {
        if (Math.cos(directionRad) < 0) {
          if (target.currentPosition.icon !== 'TACHO_REST') {
            markerHTML = `<div style="transform: scale(-1, 1)">${markerHTML}</div>`;
          }
        }
        const directionDeg = radToDeg(directionRad);
        markerHTML += getMarkerDirection(directionDeg);
      }
      return markerHTML;
    }

    fullTargets?.forEach((fullTarget) => {
      if (
        !fullTarget ||
        !fullTarget.target ||
        !fullTarget.currentPosition ||
        !isValidLatLong(fullTarget.currentPosition)
      ) {
        return;
      }

      const followedTargetClass = selectedTargetId == fullTarget.target.id ? 'selected-target-marker ' : '';
      const selectedTargetClass = ctx?.popupTargetId === fullTarget.target.id ? 'selected-tracking-target-icon' : '';

      const targetIcon = L.divIcon({
        html: `<div data-qa-element-id="target_icon_${fullTarget.target.name}">${getMarkerHtml(fullTarget)}</div>`,
        iconSize: [MARKER_ICON_SIZE, MARKER_ICON_SIZE],
        iconAnchor: [12, 12],
        popupAnchor: [0, 0],
        className: `${followedTargetClass} ${selectedTargetClass}`,
      });

      const directionRad = fullTarget.currentPosition.direction;
      const targetDirectionDown = !isNaN(directionRad) && Math.sin(directionRad) < 0;
      const tooltip = new L.Tooltip({
        className: 'target-marker-tooltip',
        content: getTargetTooltipContent(fullTarget.target, fullTarget.currentPosition),
        direction: targetDirectionDown ? 'top' : 'bottom',
        offset: targetDirectionDown ? [5, -12] : [5, 20],
        opacity: 0.9,
        permanent: true,
        interactive: true,
      });

      if (markers.current.get(fullTarget.target.id)) {
        const marker = markers.current.get(fullTarget.target.id);
        if (!marker) {
          console.log(`marker problem for ${fullTarget.target.id}, missing marker: ${marker}`);
          return;
        }

        try {
          const lastPosition = marker.getLatLng();
          if (
            lastPosition.lat !== fullTarget.currentPosition.latitude ||
            lastPosition.lng !== fullTarget.currentPosition.longitude
          ) {
            marker.setLatLng([fullTarget.currentPosition.latitude, fullTarget.currentPosition.longitude]);
          }
          marker.setIcon(targetIcon);
          // ha villog, akkor vissza kell terni az elozo megoldashoz
          // uj markernel csinalni tooltip-et
          // meglevo markernel pedig modositani a meglevot
          marker.bindTooltip(tooltip);
          setupTooltipElement(tooltip, fullTarget.target.id, fullTarget.target.name);
        } catch (e) {
          // a map variable in leaflet code sometimes initializes slower then needed PLUTO-1723
          console.error('Failed to update marker', e);
          window.location.reload();
          return;
        }
      } else {
        const marker = new L.Marker([fullTarget.currentPosition.latitude, fullTarget.currentPosition.longitude], {
          icon: targetIcon,
        });

        marker.on('tooltipopen', (event) => {
          setupTooltipElement(event.tooltip, fullTarget.target.id, fullTarget.target.name);
          bringTargetTooltipsToFront();
        });

        marker.on('dblclick', (e) => {
          map.setView(e.target.getLatLng(), 18);
          if (isPhoneOrHighScreen()) {
            e.target.openPopup();
          }
          setSelectedTargetId(fullTarget.target.id);
        });
        marker.bindTooltip(tooltip);
        marker.on('click', () => {
          ctx?.setPopupTargetId(fullTarget.target.id);
        });
        markers.current.set(fullTarget.target.id, marker);
        marker.addTo(clusterLayer);
        setupTooltipElement(tooltip, fullTarget.target.id, fullTarget.target.name);
      }
    });

    bringTargetTooltipsToFront();

    let timer: NodeJS.Timeout | undefined;
    function handleResize() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          map.invalidateSize(true);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_e) {
          // ezzel nem tudunk mit csinalni...
        }
        if (ctx?.popupTargetId) {
          const marker = markers.current.get(ctx.popupTargetId);
          marker?.getPopup()?.update();
        }
        timer = undefined;
      }, 100);
    }
    map.addEventListener('resize', handleResize);

    return () => {
      map.removeEventListener('resize', handleResize);
    };
  }, [fullTargets, selectedTargetId, ctx?.popupTargetId, map, clusterLayer]);

  return null;
}
